/* eslint-disable */
// COPIA del motor de simulación del backend (backend/utils/transferEngine.js) para correr la
// simulación en el navegador. Si cambia el del backend, re-copiar. Ver docs/ARQUITECTURA_CARRERA.md
// Lógica de ofertas de transferencia (placebo: solo visual, sin impacto en stats).

import { sortStandings } from './seasonEngine';
function clamp(value, min, max) {
    return Math.min(max, Math.max(min, value));
}

function randomInt(min, max) {
    return Math.floor(Math.random() * (max - min + 1)) + min;
}

// Estima los minutos promedio que jugaría un jugador con `rating` en un club de `estrellas`,
// usando la misma fórmula del matchEngine. Sirve para advertir al usuario que una oferta
// de un club demasiado top le va a dar muy pocos minutos.
function expectedMinutes(rating, estrellas) {
    const expectedRating = 40 + (estrellas || 1) * 10;
    const gap = rating - expectedRating;
    return clamp(Math.round(75 + gap * 1.5), 0, 90);
}

// Valor de mercado del jugador en millones de USD: combina rating, edad (pico a 26-28) y
// estrellas del club. La curva es muy empinada en rating (exponente 4.5): una media de 80
// vale ~30M, 90 ronda los 100M y solo los cracks de 95+ se acercan a los 200M+.
function computeMarketValue(rating, edad, estrellas) {
    const ratingFactor = Math.pow(clamp((rating - 45) / 54, 0, 1), 4.5);
    let ageFactor;
    if (edad <= 19) ageFactor = 1.25;
    else if (edad <= 25) ageFactor = 1.35;
    else if (edad <= 28) ageFactor = 1.2;
    else if (edad <= 31) ageFactor = 0.85;
    else if (edad <= 34) ageFactor = 0.45;
    else ageFactor = 0.15;
    const clubFactor = 0.7 + 0.3 * ((estrellas || 1) / 5);
    const base = ratingFactor * ageFactor * clubFactor * 163;
    return clamp(Math.round(base), 0, 250);
}

// Cuántas ofertas llegan esta ventana (0 a 3), con más chances cuanto mayor la valoración.
function rollOfertaCount(rating) {
    const bonus = clamp((rating - 50) / 49, 0, 1) * 0.2;
    const r = Math.random();
    if (r < 0.35 - bonus) return 0;
    if (r < 0.70 - bonus * 0.5) return 1;
    if (r < 0.92) return 2;
    return 3;
}

// Monto de la oferta en millones de USD. Se ata al VALOR DE MERCADO real del jugador (que ya
// pondera la edad), ajustado por el "poder" del club: los grandes pagan sobreprecio, los chicos
// no llegan a las cifras altas. Antes el monto ignoraba la edad y un jugador veterano de 20M de
// valor recibía ofertas de 130M; ahora la oferta nunca se despega tanto del valor de mercado.
function computeMonto(club, rating, edad) {
    const market = computeMarketValue(rating, edad, club.estrellas);
    const clubPull = 0.5 + 0.5 * Math.pow((club.estrellas || 1) / 5, 1.3); // ~0.56 (1★) .. 1.0 (5★)
    const monto = market * clubPull * (0.85 + Math.random() * 0.45);
    return clamp(Math.round(monto), 1, 250);
}

// Selección ponderada sin reemplazo: favorece clubes con estrellas cercanas al nivel del jugador.
function pickWeightedDistinct(candidates, count) {
    const pool = [...candidates];
    const chosen = [];

    for (let i = 0; i < count && pool.length > 0; i++) {
        const totalWeight = pool.reduce((sum, c) => sum + c.weight, 0);
        let roll = Math.random() * totalWeight;
        let idx = 0;
        for (; idx < pool.length; idx++) {
            roll -= pool[idx].weight;
            if (roll <= 0) break;
        }
        idx = Math.min(idx, pool.length - 1);
        chosen.push(pool[idx]);
        pool.splice(idx, 1);
    }

    return chosen;
}

// Genera entre `minimo` y 3 ofertas de traspaso para el jugador, excluyendo su club actual.
// Si `withPosicion` es true, incluye la posición actual de cada club en su tabla.
function generateTransferOffers({ leagues, clubActual, rating, edad = 25, withPosicion = false, minimo = 0, posicionFinal = false }) {
    // Curva más generosa: un jugador de media ~82 ya apunta a clubes de ~4.5 estrellas,
    // así los grandes se interesan sin necesidad de llegar a 90+.
    const targetEstrellas = clamp(0.8 + (rating - 45) * (4.4 / 45), 0.8, 5);

    const candidates = [];
    for (const league of leagues) {
        for (const club of league.clubs) {
            if (club.tm_id === clubActual.tm_id) continue;
            const diff = Math.abs((club.estrellas || 1) - targetEstrellas);
            // Caída de peso pronunciada: los clubes muy por debajo de tu nivel quedan
            // prácticamente descartados (no tiene sentido que un club flojo fiche a un crack).
            const weight = Math.max(0.015, 1 - diff / 2.2);
            candidates.push({ club, league, weight });
        }
    }

    const numOfertas = Math.max(minimo, rollOfertaCount(rating));
    const chosen = pickWeightedDistinct(candidates, numOfertas);

    return chosen.map(({ club, league }) => {
        const offer = {
            tm_id: club.tm_id,
            nombre: club.nombre,
            logo: club.logo,
            pais: league.pais,
            division: league.division,
            liga_nombre: league.nombre,
            estrellas: club.estrellas,
            posicion_en_liga: null,
            // true: la posición es la TABLA FINAL de la temporada que terminó (al fichar
            // arranca una temporada nueva, así que el club no estará ahí todavía).
            // false: es la posición ACTUAL en la tabla en curso (ofertas de mitad de temporada).
            posicion_es_final: posicionFinal,
            monto_millones: computeMonto(club, rating, edad),
            minutos_esperados: expectedMinutes(rating, club.estrellas),
            anios_contrato: randomInt(1, 5),
            es_renovacion: false,
        };

        if (withPosicion) {
            const tabla = sortStandings(league.clubs);
            offer.posicion_en_liga = tabla.findIndex(c => c.tm_id === club.tm_id) + 1;
        }

        return offer;
    });
}

// ¿El club quiere renovarle el contrato al jugador? No es automático: depende de cuánto
// rinde respecto al nivel del club y de la edad. Un crack por encima del nivel del club casi
// siempre se renueva; a un veterano (o a alguien que el club ya no necesita) puede no
// ofrecerle renovación y obligarlo a buscar otro equipo.
function clubWantsRenewal({ rating, edad, estrellas }) {
    const expectedRating = 40 + (estrellas || 1) * 10;
    let p = 0.6;
    p += clamp((rating - expectedRating) / 80, -0.35, 0.3); // por encima del nivel del club → lo retienen
    if (edad >= 36) p -= 0.5;
    else if (edad >= 33) p -= 0.28;
    else if (edad <= 27) p += 0.1;
    return Math.random() < clamp(p, 0.05, 0.95);
}

// Oferta de RENOVACIÓN del club actual: mismo club, sin traspaso (monto 0), con un nuevo
// contrato de 1 a 5 años. Solo se ofrece si el club quiere renovar (ver clubWantsRenewal).
function buildRenewalOffer({ clubActual, leagues, rating }) {
    const league = leagues.find(l => l.pais === clubActual.pais && l.division === clubActual.division);
    const estrellas = clubActual.estrellas || 1;
    return {
        tm_id: clubActual.tm_id,
        nombre: clubActual.nombre,
        logo: clubActual.logo,
        pais: clubActual.pais,
        division: clubActual.division,
        liga_nombre: league?.nombre || '',
        estrellas,
        posicion_en_liga: null,
        monto_millones: 0,
        minutos_esperados: expectedMinutes(rating, estrellas),
        anios_contrato: randomInt(1, 5),
        es_renovacion: true,
    };
}

export { generateTransferOffers, buildRenewalOffer, clubWantsRenewal, computeMarketValue, expectedMinutes };